import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStateMachine } from 'little-state-machine'
import { updateStore } from '../../../store'
import { usePro } from '../../../hooks'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'
import { CheckCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/solid'
import { formatPrizeTypeLabel, INCENTIVE_BASIS_LABEL } from '../../../utils/incentiveDisplay'
import styles from './SearchProCard.module.scss'

// ── 헬퍼 함수 ──

const getCareerLevelLabel = (level) => {
	if (!level) return ''
	const map = {
		NEWBIE: '신입',
		ONE_TO_THREE: '1년 ~ 3년차',
		FOUR_TO_SIX: '4년 ~ 6년차',
		SEVEN_TO_TEN: '7년 ~ 10년차',
		OVER_TEN: '10년 이상',
	}
	return map[level] || level
}

const formatCurrency = (amount) => {
	if (amount === null || amount === undefined) return '-'
	try {
		return `${Number(amount).toLocaleString('ko-KR')}원`
	} catch (_) {
		return `${amount}`
	}
}

const formatExpenseReimbursementPercent = (value) => {
	if (value === null || value === undefined) return '-'
	try {
		return `${Number(value)}%`
	} catch (_) {
		return `${value}`
	}
}

const formatYesNo = (val) => (val ? '가능' : '불가능')

const formatPayroll = (cycle) => {
	switch (cycle) {
		case 'weekly':
			return '주급'
		case 'monthly':
			return '월급'
		case 'perGame':
			return '경기당'
		default:
			return cycle || '-'
	}
}

const formatTaxIncluded = (included) => {
	if (included === undefined || included === null) return '-'
	return included ? '세후' : '세전'
}

const formatIncentiveRange = (startRank, endRank) => {
	if (startRank === 1 && endRank === 1) return '우승'
	const endLabel = endRank === 0 ? 'CUT' : endRank
	return `${startRank} ~ ${endLabel}`
}

const sortIncentives = (incentives) => {
	if (!Array.isArray(incentives) || incentives.length === 0) return []
	return [...incentives].sort((a, b) => {
		if (a.startRank !== b.startRank) return a.startRank - b.startRank
		const aEnd = a.endRank === 0 ? 9999 : a.endRank
		const bEnd = b.endRank === 0 ? 9999 : b.endRank
		return aEnd - bEnd
	})
}

const formatTourType = (type) => {
	if (!type) return ''
	if (type === 'KPGA') return 'KPGA'
	if (type === 'KLPGA') return 'KLPGA'
	return type
}

// ── 컴포넌트 ──

export const SearchProCard = ({
	pro,
	attendanceLabel = null,
	selectionMode = false,
	selected = false,
	onSelect,
}) => {
	const [activeContractTab, setActiveContractTab] = useState('domestic')
	const [isDetailOpen, setIsDetailOpen] = useState(false)
	const navigate = useNavigate()
	const { actions, state } = useStateMachine({ updateStore })

	// 상세보기 열릴 때만 프로 상세 데이터 lazy-load (계약 정보 포함)
	const { data: proDetail, isLoading: proDetailLoading } = usePro(pro?.id, isDetailOpen)

	const utp = pro?.user_tour_pro

	// 계약 데이터: 리스트 응답에 있으면 사용, 없으면 상세 API 응답에서 가져옴
	const listContract = pro?.user_player_contract?.[0]
	const detailContract = proDetail?.user?.user_player_contract?.[0]
	const preferred = listContract || detailContract
	const domestic = preferred?.domesticContract
	const international = preferred?.overseasContract

	const sponsorName = utp?.main_sponsor?.name || utp?.main_sponsor || null
	const agencyName = utp?.agency?.name || utp?.agency_name || null
	const top10Count = utp?.top10_count || 0
	const cutThroughRate = utp?.cut_through_rate || 0

	// 프로 정보 Key-Value
	const proInfoKeyValues = useMemo(() => {
		if (!utp) return []
		const rows = []
		if (utp.tour_type) rows.push({ key: '소속협회', value: formatTourType(utp.tour_type) })
		if (utp.full_membership_number)
			rows.push({ key: '회원번호', value: utp.full_membership_number })
		if (utp.career_level)
			rows.push({ key: '경력', value: getCareerLevelLabel(utp.career_level) })
		if (sponsorName) rows.push({ key: '메인스폰서', value: sponsorName })
		if (agencyName) rows.push({ key: '에이전시', value: agencyName })
		if (top10Count > 0) rows.push({ key: 'TOP 10', value: `${top10Count}회` })
		if (cutThroughRate > 0) rows.push({ key: '컷 통과율', value: `${cutThroughRate}%` })
		return rows
	}, [utp, sponsorName, agencyName, top10Count, cutThroughRate])

	// 국내 계약 Key-Value
	const domesticKeyValues = useMemo(() => {
		if (!domestic) return []
		return [
			{ key: '세금 포함 여부', value: formatTaxIncluded(domestic.isSalaryTaxIncluded) },
			{ key: '임금 지급 주기', value: formatPayroll(domestic.payrollCycles) },
			{ key: '컷 통과', value: formatCurrency(domestic.weeklySalaryCutThrough) },
			{ key: '컷 아웃', value: formatCurrency(domestic.weeklySalaryCutFail) },
			{ key: '제주 대회 참여', value: formatYesNo(domestic.jejuTournamentParticipation) },
			{ key: '제주 지원금', value: formatCurrency(domestic.expenseReimbursementJeju) },
			{ key: INCENTIVE_BASIS_LABEL, value: formatPrizeTypeLabel(domestic?.prizeType) },
			{
				key: '인센티브 금액',
				...(domestic?.incentives?.length > 0
					? {
							incentiveLines: sortIncentives(domestic.incentives).map(
								(i) =>
									`${formatIncentiveRange(i.startRank, i.endRank)} : ${i.percentage}%`,
							),
						}
					: { value: '-' }),
			},
			{ key: '스폰서십 모자', value: formatYesNo(domestic.wearSponsoredHat) },
		]
	}, [domestic])

	// 해외 계약 Key-Value
	const internationalKeyValues = useMemo(() => {
		if (!international) return []
		return [
			{ key: '세금 포함 여부', value: formatTaxIncluded(international.isSalaryTaxIncluded) },
			{ key: '임금 지급 주기', value: formatPayroll(international.payrollCycles) },
			{ key: '컷 통과', value: formatCurrency(international.weeklySalaryCutThrough) },
			{ key: '컷 아웃', value: formatCurrency(international.weeklySalaryCutFail) },
			{ key: INCENTIVE_BASIS_LABEL, value: formatPrizeTypeLabel(international?.prizeType) },
			{
				key: '인센티브 금액',
				...(international?.incentives?.length > 0
					? {
							incentiveLines: sortIncentives(international.incentives).map(
								(i) =>
									`${formatIncentiveRange(i.startRank, i.endRank)} : ${i.percentage}%`,
							),
						}
					: { value: '-' }),
			},
			{ key: '스폰서십 모자', value: formatYesNo(international.wearSponsoredHat) },
			{
				key: '해외 지원금',
				value: formatExpenseReimbursementPercent(international.expenseReimbursement),
			},
		]
	}, [international])

	const handleOfferClick = () => {
		if (selectionMode && onSelect) {
			onSelect()
			return
		}
		actions.updateStore({
			offerInput: {
				...state.offerInput,
				targetUserId: pro?.id,
				selectedPro: pro,
			},
		})
		navigate('/search?tab=partner')
	}

	if (!pro) return null

	const typeLabel = formatTourType(utp?.tour_type)
	const careerLevel = getCareerLevelLabel(utp?.career_level)
	const hasDomestic = domesticKeyValues.length > 0
	const hasInternational = internationalKeyValues.length > 0
	const hasProInfo = proInfoKeyValues.length > 0
	const hasMeta = sponsorName || agencyName

	return (
		<div className={`${styles.card} ${selected ? styles.cardSelected : ''}`}>
			{/* 출전 라벨 (대회 선택 시) */}
			{attendanceLabel && (
				<div
					className={
						attendanceLabel === '출전 예정'
							? styles.attendanceLabelExpected
							: styles.attendanceLabelUnconfirmed
					}>
					{attendanceLabel === '출전 예정' ? (
						<CheckCircleIcon className={styles.attendanceLabelIcon} />
					) : (
						<QuestionMarkCircleIcon className={styles.attendanceLabelIcon} />
					)}
					<span>{attendanceLabel}</span>
				</div>
			)}

			{/* 프로필 이미지 */}
			<div className={styles.imageWrap}>
				{careerLevel && <span className={styles.careerLevelBadge}>{careerLevel}</span>}
				<img
					src={pro.profile_image || '/images/pro-default.png'}
					alt={`${pro.name} 프로필`}
					onError={(e) => {
						e.target.src = '/images/pro-default.png'
					}}
				/>
			</div>

			<div className={styles.body}>
				<div className={styles.header}>
					{/* 투어 타입 */}
					{typeLabel && <span className={styles.typeLabel}>{typeLabel}</span>}

					{/* 이름 */}
					<div className={styles.nameRow}>
						<h2 className={styles.name}>{pro.name} 프로</h2>
					</div>

					{/* 주요 성적 — 없으면 0회 0%로 표시 */}
					<div className={styles.statsWrap}>
						<span className={styles.statsLabel}>주요 성적</span>
						<div className={styles.statsRow}>
							<div className={styles.statItem}>
								<span className={styles.statValue}>{top10Count}회</span>
								<span className={styles.statKey}>TOP10 달성</span>
							</div>
							<div className={styles.statDivider} />
							<div className={styles.statItem}>
								<span className={styles.statValue}>{cutThroughRate}%</span>
								<span className={styles.statKey}>컷 통과율</span>
							</div>
						</div>
					</div>

					{/* 스폰서 / 에이전시 */}
					{hasMeta && (
						<div className={styles.metaRow}>
							{sponsorName && (
								<span className={styles.metaItem}>스폰서: {sponsorName}</span>
							)}
							{sponsorName && agencyName && (
								<span className={styles.metaDot}> · </span>
							)}
							{agencyName && (
								<span className={styles.metaItem}>에이전시: {agencyName}</span>
							)}
						</div>
					)}
				</div>

				{/* 자세히 보기 / 접기 — 캐디 카드와 동일 스타일 (텍스트 버튼 + 블러, 가운데) */}
				<button
					type="button"
					className={styles.detailToggle}
					onClick={() => setIsDetailOpen(!isDetailOpen)}
					aria-expanded={isDetailOpen}>
					{isDetailOpen ? '접기' : '자세히 보기'}
					{isDetailOpen ? (
						<ChevronUpIcon className={styles.detailToggleIcon} />
					) : (
						<ChevronDownIcon className={styles.detailToggleIcon} />
					)}
				</button>

				{/* 상세 콘텐츠 */}
				{isDetailOpen && (
					<div className={styles.detailContent}>
						{/* 로딩 중 */}
						{proDetailLoading && !listContract && (
							<div className={styles.detailLoading}>불러오는 중...</div>
						)}

						{/* 프로 정보 */}
						{hasProInfo && (
							<div className={styles.section}>
								<h3 className={styles.sectionTitle}>프로 정보</h3>
								<div className={styles.keyValues}>
									{proInfoKeyValues.map((item, idx) => (
										<div key={idx} className={styles.keyValueRow}>
											<span className={styles.key}>{item.key}</span>
											<span className={styles.value}>{item.value}</span>
										</div>
									))}
								</div>
							</div>
						)}

						{/* 선호계약정보 */}
						{(hasDomestic || hasInternational) && (
							<div className={styles.section}>
								<h3 className={styles.sectionTitle}>선호계약정보</h3>
								<div className={styles.contractTabs}>
									{hasDomestic && (
										<button
											type="button"
											className={`${styles.contractTab} ${activeContractTab === 'domestic' ? '' : styles.contractTabInactive}`}
											onClick={() => setActiveContractTab('domestic')}>
											국내
										</button>
									)}
									{hasInternational && (
										<button
											type="button"
											className={`${styles.contractTab} ${activeContractTab === 'international' ? '' : styles.contractTabInactive}`}
											onClick={() => setActiveContractTab('international')}>
											해외
										</button>
									)}
								</div>
								<div className={styles.keyValues}>
									{(activeContractTab === 'domestic'
										? domesticKeyValues
										: internationalKeyValues
									).map((item, idx) => (
										<div
											key={idx}
											className={
												item.incentiveLines
													? styles.keyValueRowBlock
													: styles.keyValueRow
											}>
											<span className={styles.key}>{item.key}</span>
											{item.incentiveLines ? (
												<ul className={styles.incentiveList}>
													{item.incentiveLines.map((line, i) => (
														<li
															key={i}
															className={styles.incentiveListItem}>
															{line}
														</li>
													))}
												</ul>
											) : (
												<span className={styles.value}>{item.value}</span>
											)}
										</div>
									))}
								</div>
							</div>
						)}

						{/* 계약 정보 없음 (로딩 끝 + 계약 데이터 없음) */}
						{!proDetailLoading && !hasDomestic && !hasInternational && (
							<div className={styles.section}>
								<h3 className={styles.sectionTitle}>선호계약정보</h3>
								<div className={styles.noContractMessage}>
									선호 계약 정보가 없습니다.
								</div>
							</div>
						)}
					</div>
				)}

				{/* 제안하기 / 선택 버튼 */}
				<button type="button" className={styles.offerButton} onClick={handleOfferClick}>
					{selectionMode ? (selected ? '선택됨' : '선택') : '제안하기'}
				</button>
			</div>
		</div>
	)
}

export default SearchProCard
